import React, { useState, useEffect, useRef } from "react";
import api from "../../utils/axiosInstance";
import { Spinner, Card, CardBody } from "../../components/ui";
import { FaUser, FaCheck, FaCamera } from "react-icons/fa";

export default function ProfileTab() {
  const [formData, setFormData] = useState({ name: "", firstName: "", lastName: "", email: "", phone: "", avatar: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get("/api/settings/profile")
      .then((res) => setFormData({ name: res.data.name || "", firstName: res.data.firstName || "", lastName: res.data.lastName || "", email: res.data.email || "", phone: res.data.phone || "", avatar: res.data.avatar || "" }))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type and size
    if (!file.type.startsWith("image/")) {
      showToast("Please select an image file", "error");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast("Image must be under 5MB", "error");
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const uploadRes = await api.post("/api/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (uploadRes.data?.url) {
        const avatarUrl = uploadRes.data.url;
        // Save avatar to profile immediately
        await api.put("/api/settings/profile", { ...formData, avatar: avatarUrl });
        setFormData((prev) => ({ ...prev, avatar: avatarUrl }));
        showToast("Profile photo updated");
      } else {
        showToast("Upload failed", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Failed to upload image", "error");
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/api/settings/profile", formData);
      showToast("Profile updated successfully");
    } catch (err) {
      console.error(err);
      showToast("Error updating profile", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-ink-muted"><Spinner size="sm" /> Loading...</div>;

  const displayName = [formData.firstName, formData.lastName].filter(Boolean).join(" ") || formData.name;
  const initials = (formData.firstName || formData.name || formData.email || "U").charAt(0).toUpperCase();

  return (
    <div>
      <h2 className="text-lg font-semibold text-ink-primary mb-5">Profile Settings</h2>

      {toast && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-2 ${
          toast.type === "error"
            ? "bg-red-500/10 text-red-500 border border-red-500/20"
            : "bg-green-500/10 text-green-500 border border-green-500/20"
        }`}>
          {toast.type === "error" ? "!" : <FaCheck size={12} />} {toast.msg}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Profile Card with Avatar Upload */}
        <Card>
          <CardBody className="flex flex-col items-center text-center py-8">
            <div className="relative group mb-4">
              {formData.avatar ? (
                <img
                  src={formData.avatar}
                  alt="Avatar"
                  className="w-20 h-20 rounded-2xl object-cover border-2 border-surface-border"
                />
              ) : (
                <div
                  className="w-20 h-20 rounded-2xl flex items-center justify-center font-bold text-2xl text-white"
                  style={{ background: "linear-gradient(135deg, #2C86E9, #0155A8)" }}
                >
                  {initials}
                </div>
              )}
              {/* Upload overlay */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="absolute inset-0 rounded-2xl flex items-center justify-center bg-black/0 group-hover:bg-black/50 transition-all cursor-pointer"
              >
                <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white">
                  {uploading ? <Spinner size="sm" /> : <FaCamera size={18} />}
                </span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarUpload}
              />
            </div>
            <p className="text-sm font-semibold text-ink-primary">{displayName || "No name set"}</p>
            <p className="text-xs text-ink-muted mt-1">{formData.email}</p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="mt-3 text-xs text-brand hover:underline cursor-pointer"
            >
              {uploading ? "Uploading..." : "Change photo"}
            </button>
          </CardBody>
        </Card>

        {/* Edit Form */}
        <div className="md:col-span-2">
          <Card>
            <CardBody>
              <h3 className="text-sm font-semibold text-ink-primary mb-4 flex items-center gap-2">
                <FaUser size={13} className="text-brand" /> Personal Information
              </h3>

              <form onSubmit={onSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="input-group">
                    <label className="input-label">First Name</label>
                    <input
                      className="input"
                      placeholder="Enter your first name"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    />
                  </div>
                  <div className="input-group">
                    <label className="input-label">Last Name</label>
                    <input
                      className="input"
                      placeholder="Enter your last name"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    />
                  </div>
                  <div className="input-group">
                    <label className="input-label">Email Address</label>
                    <input
                      className="input"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div className="input-group">
                    <label className="input-label">Phone Number</label>
                    <input
                      className="input"
                      type="tel"
                      placeholder="Enter your phone number"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                </div>

                <button className="btn-primary flex items-center gap-2" type="submit" disabled={saving}>
                  {saving ? <Spinner size="sm" /> : <FaCheck size={11} />}
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </form>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
